import { useState } from 'react';
import { contactInfo } from '@/mocks/about';

export default function Contact() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u3kal3pcjqs2fcslcg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = (parsed && (parsed as Record<string, unknown>).meta && ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message) || responseText;
        setFormStatus('error');
        setFormError(String(serverMsg || '提交失败，请稍后重试'));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            联系我们
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
            有任何问题或合作意向，欢迎随时与我们取得联系
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-lg font-semibold text-foreground-950 mb-6">
              联系方式
            </h3>
            <div className="flex flex-col gap-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-950">电子邮箱</div>
                  <a href={`mailto:${contactInfo.email}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors duration-200">
                    {contactInfo.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-phone-line text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-950">客服热线</div>
                  <a href={`tel:${contactInfo.phone}`} className="text-sm text-foreground-600 hover:text-primary-600 transition-colors duration-200">
                    {contactInfo.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-950">公司地址</div>
                  <p className="text-sm text-foreground-600">{contactInfo.address}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-950">微信公众号</div>
                  <p className="text-sm text-foreground-600">{contactInfo.wechat}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-600 flex-shrink-0">
                  <i className="ri-time-line text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-950">工作时间</div>
                  <p className="text-sm text-foreground-600">{contactInfo.workingHours}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-foreground-950 mb-6">
              发送消息
            </h3>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-readdy-form="">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  您的姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                  placeholder="请输入您的姓名"
                />
              </div>

              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  电子邮箱 <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                  placeholder="请输入您的邮箱"
                />
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200"
                  placeholder="请输入消息主题"
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-2.5 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-950 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all duration-200 resize-none"
                  placeholder="请输入您的留言内容，最多500字"
                ></textarea>
              </div>

              <div className="anti-spam-field">
                <input
                  name="phone_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formStatus === 'success' && (
                <div className="p-3 rounded-md bg-accent-100 text-accent-800 text-sm">
                  消息发送成功！我们会尽快回复您。
                </div>
              )}

              {formStatus === 'error' && (
                <div className="p-3 rounded-md bg-red-100 text-red-800 text-sm">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                className="inline-flex items-center justify-center px-6 py-2.5 text-sm font-semibold rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                发送消息
                <i className="ri-send-plane-line ml-2"></i>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}